import classNames from "classnames"
import type { CSSProperties } from "react"

import VideoBlock from "@/pages/TrackingPage/ThemeLayoutCustomize/components/VideoBlock/VideoBlock"

import ImageGrid from "../ImageGrid/ImageGrid"
import TrackingBlock from "../TrackingBlock/TrackingBlock"
import { useBackdropThemeLayout } from "./ThemeLayoutLeftTabPanels/BackdropThemeLayoutContext"
import {
  backdropDesktopBackgroundCss,
  mapBottomImagesToImageGridItems,
} from "./ThemeLayoutLeftTabPanels/themeLayoutBackdropEditor"
import type { ThemeLayoutPreviewViewport } from "./themeLayoutPreviewViewport"

type BackdropPreviewCardPosition = "CENTER" | "LEFT" | "RIGHT"

const cardRowClassByPosition: Record<BackdropPreviewCardPosition, string> = {
  CENTER: "justify-center",
  LEFT: "justify-start",
  RIGHT: "justify-end",
}

const containerClassname = "mx-auto flex w-full max-w-[1176px] px-4 h-full"

const trackingMaxWClass: Record<"LARGE" | "SMALL" | "STANDARD", string> = {
  LARGE: "max-w-[600px]",
  SMALL: "max-w-[360px]",
  STANDARD: "max-w-[440px]",
}

const cardRadiusClass = (displayCornerRadius: "ROUNDED" | "SHARP" | "STANDARD"): string => {
  if (displayCornerRadius === "SHARP") {
    return "rounded-none"
  }
  if (displayCornerRadius === "ROUNDED") {
    return "rounded-2xl"
  }
  return "rounded-lg"
}

export interface ThemeLayoutPreviewBackdropProps {
  viewport: ThemeLayoutPreviewViewport
}

/**
 * Backdrop 预览：与左侧 Backdrop 草稿（内容 + 样式）联动；移动端 Banner 用 themeImageBanner。
 */
const ThemeLayoutPreviewBackdrop = ({ viewport }: ThemeLayoutPreviewBackdropProps) => {
  const { draft, bottomSectionPopupDraft } = useBackdropThemeLayout()
  const isMobile = viewport === "mobile"
  const { content, style } = draft

  const { position } = style
  const rowJustify = cardRowClassByPosition[position]
  const desktopBg = backdropDesktopBackgroundCss(style.desktop_bg_color)
  const radius = cardRadiusClass(style.corner_radius)
  const trackingMaxW = trackingMaxWClass[style.size]

  const bannerSrc = content.image_banner?.url?.trim() ?? ""
  const hasBannerWithoutUrl = Boolean(content.image_banner?.shopifyFileId?.trim()) && !bannerSrc
  const hideMobileBanner = content.mobile_layout === "HIDE"

  // popup 打开时使用 overlay 数据做实时预览，关闭后回退到 store 数据
  const bottom = bottomSectionPopupDraft ?? content.bottom_section
  const gridItems = mapBottomImagesToImageGridItems(bottom.images)

  const bottomVideo = bottom.video
  const videoRaw = bottomVideo?.youtubeUrl?.trim()
  const shouldShowVideo = bottomVideo !== undefined && bottomVideo.enabled !== false

  const desktopBannerAreaStyle: CSSProperties = {
    ...(bannerSrc
      ? {
        backgroundImage: `url(${JSON.stringify(bannerSrc)})`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
      }
      : {}),
    ...(hasBannerWithoutUrl ? { backgroundColor: "#F1F1F1" } : {}),
  }

  return (
    <div
      className={classNames(
        "flex min-h-0 w-full min-w-0 flex-1 flex-col gap-[40px] pb-6",
      )}
    >
      {isMobile ? (
        <div>
          {!hideMobileBanner && bannerSrc ? (
            <div className="relative h-[240px] w-full shrink-0 overflow-hidden">
              <img
                alt=""
                className="block h-[240px] w-full object-cover"
                decoding="async"
                loading="lazy"
                src={bannerSrc}
              />
            </div>
          ) : !hideMobileBanner && hasBannerWithoutUrl ? (
            <div
              aria-hidden
              className="relative h-[240px] min-h-[120px] w-full shrink-0"
            />
          ) : null}
          <div className={classNames(containerClassname, "flex justify-center pt-[30px]")}>
            <div className={classNames("w-full min-h-0", trackingMaxW)}>
              <div className={classNames(radius)}>
                <TrackingBlock hideCardBorder isMobile />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full  min-h-[640px]" style={desktopBannerAreaStyle}>
          <div
            className={classNames(
              containerClassname,
              "py-[70px]",
              rowJustify,
            )}
          >
            <div
              className={classNames(
                "w-full min-h-0 py-[30px] px-5",
                trackingMaxW,
                "overflow-hidden border border-solid border-[#E6E6E6]",
                radius,
              )}
              style={{
                backgroundColor: desktopBg,
              }}
            >
              <TrackingBlock />
            </div>
          </div>
        </div>
      )}

      {
        !!gridItems.length && (
          <div className={classNames(containerClassname)}>
            <ImageGrid
              cornerRadiusClass={radius}
              items={gridItems.length > 0 ? gridItems : undefined}
              ratioMode={bottom.imageRatio}
              viewport={isMobile ? "mobile" : "desktop"}
            />
          </div>
        )
      }
      {shouldShowVideo ? (
        <div className={classNames(containerClassname)}>
          <VideoBlock cornerRadiusClass={radius} youtubeUrl={videoRaw || ""} />
        </div>
      ) : null}
    </div>
  )
}

export default ThemeLayoutPreviewBackdrop
